import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { zodResolver } from '@hookform/resolvers/zod';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import IconButton from '@mui/material/IconButton';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Typography from '@mui/material/Typography';
import { useEffect } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { FormattedMessage } from 'react-intl';
import Icon from '@mdi/react';
import { mdiCheck, mdiClose, mdiTable, mdiTune } from '@mdi/js';
import { theme } from '../../theme';
import { FilteringFormConditionsContent } from '../FilteringFormConditionsContent';
import { useFilterPreviewCount } from '../useFilterPreviewCount';
import { andConditionsSchema } from '../filterSchemas';
import { isEmptyFilter } from '../helpers';
import { conditionsDefault } from '../../api';
const emptyCondition = {
    orConditions: [
        {
            field: '',
            operator: undefined,
            value: undefined,
        },
    ],
};
const dialogDefaultValues = {
    andConditions: [emptyCondition],
};
function getDefaultValues(conditions) {
    if (isEmptyFilter(conditions)) {
        return conditionsDefault;
    }
    const res = andConditionsSchema.safeParse(conditions);
    return res.success ? res.data : conditionsDefault;
}
export function FilterFormDialog({ open, onClose, onApply, filterColumns, enableAutocomplete, initialValues, }) {
    const form = useForm({
        defaultValues: getDefaultValues(initialValues ?? null),
        mode: 'onChange',
        resolver: zodResolver(andConditionsSchema),
    });
    const { reset, handleSubmit, getValues, formState, control } = form;
    const { count: previewCount, isLoading: previewCountLoading } = useFilterPreviewCount(control, open);
    useEffect(() => {
        if (open) {
            const hasInitial = initialValues?.andConditions?.length && initialValues.andConditions.every((g) => g.orConditions?.length);
            reset(hasInitial ? initialValues : dialogDefaultValues);
        }
    }, [open, reset, initialValues]);
    const handleApply = () => {
        const values = getValues();
        if (values.andConditions.length > 0) {
            onApply(values);
            onClose();
        }
    };
    const handleClearAll = () => {
        reset(dialogDefaultValues);
        onApply({ andConditions: [] });
        onClose();
    };
    return (_jsxs(Dialog, { open: open, onClose: onClose, maxWidth: false, PaperProps: {
            sx: {
                width: 'auto',
                minWidth: 'min(850px, 70vw)',
                minHeight: 'min(400px, 70vh)',
                maxWidth: '90vw',
                maxHeight: '90vh',
            },
        }, children: [_jsxs(DialogTitle, { sx: { display: 'flex', alignItems: 'center', gap: 1, py: 1.25, px: 2, fontSize: '1rem', flexShrink: 0 }, children: [_jsx(Icon, { path: mdiTune, size: 1, style: { color: theme.vars.palette.primary.main } }), _jsx(FormattedMessage, { id: "tableFiltering.addFilter.dialogTitle", defaultMessage: "Add filter" }), _jsx(IconButton, { onClick: onClose, size: "small", sx: { ml: 'auto' }, children: _jsx(Icon, { path: mdiClose, size: 1 }) })] }), _jsx(FormProvider, { ...form, children: _jsxs("form", { onSubmit: (e) => {
                        e.preventDefault();
                        handleSubmit(handleApply)(e);
                    }, style: { display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }, children: [_jsx(DialogContent, { dividers: true, sx: { flex: 1, overflow: 'auto', minHeight: 0 }, children: _jsx(FilteringFormConditionsContent, { filterColumns: filterColumns, enableAutocomplete: enableAutocomplete, showApplyButton: false }) }), _jsxs(DialogActions, { sx: { flexShrink: 0, justifyContent: 'space-between', px: 2 }, children: [_jsx(Box, { sx: { display: 'flex', alignItems: 'center', gap: 1 }, children: (previewCount != null || previewCountLoading) && (_jsxs(Box, { sx: { display: 'flex', alignItems: 'center', gap: 0.75 }, children: [_jsx(Icon, { path: mdiTable, size: 0.9, style: { color: theme.vars.palette.text.secondary } }), _jsx(Typography, { variant: "body2", color: "text.secondary", children: previewCountLoading ? (_jsx(FormattedMessage, { id: "tableFiltering.previewCount.loading", defaultMessage: "Counting\u2026" })) : (_jsx(FormattedMessage, { id: "tableFiltering.previewCount.result", defaultMessage: "{count, plural, one {# row} other {# rows}} match", values: { count: previewCount ?? 0 } })) })] })) }), _jsxs(Box, { sx: { display: 'flex', gap: 1 }, children: [_jsx(Button, { onClick: handleClearAll, variant: "outlined", color: "modifyGrey", children: _jsx(FormattedMessage, { id: "tableFiltering.clearAll.button", defaultMessage: "Clear all" }) }), _jsx(Button, { type: "submit", variant: "contained", disabled: !formState.isValid, color: "primary", startIcon: _jsx(Icon, { path: mdiCheck, size: 0.8 }), children: _jsx(FormattedMessage, { id: "tableFiltering.applyFilters.button", defaultMessage: "Apply filters" }) })] })] })] }) })] }));
}
//# sourceMappingURL=FilterFormDialog.js.map